import React, { useEffect, useState } from "react";
import { useAppSelector } from "../services/store";
import LocalImages from '../static';
import { patchLikeOnObservation, patchLikeOnPost } from "src/shared/services/src";
import { ChildrenStoreData, ReactionCommentData } from "src/shared/types/src";
import { getCurrentChild } from "src/shared/redux-state/src";
import { ENTITY_TYPE } from "src/shared/config/src";
import { Heart } from "react-ionicons";
import { CommentsModal, LikesListModal } from "./modals";
import { handleError } from "../utilities/helpers";
import { FaThumbsUp } from "react-icons/fa";

type LikeCommentFooterProps = {
  likeCount: number;
  commentCount: number;
  entityId: string;
  likedByMe: boolean;
  disableFooterInteraction?: boolean
  entityType: string
  onLikeUnlike?: Function,
  onAddComment?: () => void;
  onDeleteComment?: (commentId: string) => void;
  canLikeComment?: boolean;
  showComment?: boolean;
  disableLikeDetailsInteraction?: boolean;
  disableCommentDetailsInteraction?: boolean;
  openLikeDetails?: boolean;
  openCommentDetails?: boolean;
  customLikeCommentStyle?: string,
  onLikeDetailsPress?: () => void;
  onCommentDetailsPress?: () => void;
}

const LikeCommentFooter: React.FC<LikeCommentFooterProps> = ({ likeCount, commentCount, entityId, likedByMe,
  disableFooterInteraction = false, entityType, onLikeUnlike, onAddComment, canLikeComment = true, showComment = true, 
  disableLikeDetailsInteraction = false, disableCommentDetailsInteraction = false, onLikeDetailsPress, onCommentDetailsPress,
  openLikeDetails = false, openCommentDetails = false,
  onDeleteComment, customLikeCommentStyle
}) => {

  const [likeCountLocal, setLikeCount] = useState<number>(likeCount);
  const [commentCountLocal, setCommentCount] = useState<number>(commentCount);
  const [postLiked, setPostLiked] = useState(likedByMe);
  const [likesListModalOpen, setLikesListModalOpen] = useState(false);
  const [commentsModalOpen, setCommentModalOpen] = useState(false);
  const [newComment, setNewComment] = useState<ReactionCommentData>();

  const currentChild: ChildrenStoreData = useAppSelector(getCurrentChild);

  const [updatedCommentsList, setUpdatedComentsList] = useState<
    ReactionCommentData[]
  >([]);

  useEffect(() => {
    setLikeCount(likeCount);
    setCommentCount(commentCount);
    setPostLiked(likedByMe);
  }, [likeCount, commentCount, likedByMe]);

  useEffect(() => {
    if (openLikeDetails) {
      setLikesListModalOpen(true);
    }
  }, [openLikeDetails]);

  useEffect(() => {
    if (openCommentDetails) {
      setCommentModalOpen(true)
    }
  }, [openCommentDetails]);

  const onLikePress = async () => {
    if (disableFooterInteraction) return;
    if (!canLikeComment) return;
    addReaction('addLike');
    setPostLiked(true);
  };

  const onUnLikePress = async () => {
    if (disableFooterInteraction) return;
    if (!canLikeComment) return;
    addReaction('removeLike');
    setPostLiked(false);
  };

  const addReaction = async (action: string) => {
    try {
      let response;
      if (entityType === ENTITY_TYPE.OBSERVATION) {
        response = await patchLikeOnObservation({ child: currentChild, observationId: entityId });
        if (response && Object.keys(response).length) {
          const updatedCount = response.likes.count || 0;
          const likedBySelf = response.likes.likedByMe || false;
          setLikeCount(updatedCount);
          setPostLiked(likedBySelf);
          // send the updated count and self like to parent component
          if (typeof onLikeUnlike == 'function') {
            onLikeUnlike({ likeCount: updatedCount, likedBySelf })
          }
        }
      } else if (entityType === ENTITY_TYPE.EYENGAGE) {
        response = await patchLikeOnPost({ child: currentChild, postId: entityId });
        if (response && Object.keys(response).length) {
          const updatedCount = response?.reactions?.likes.count || 0;
          const likedBySelf = response?.reactions?.likes?.self || false;
          setLikeCount(updatedCount);
          setPostLiked(likedBySelf);
          // send the updated count and self like to parent component
          if (typeof onLikeUnlike == 'function') {
            onLikeUnlike({ likeCount: updatedCount, likedBySelf })
          }
        }
      }
    } catch (error: any) {
      handleError(error);
      setPostLiked(action !== 'addLike');
    }
  };

  const handleAddComment = async (commentObj: ReactionCommentData | ReactionCommentData[]) => {
    if (entityType === ENTITY_TYPE.OBSERVATION) {
      setNewComment(commentObj as ReactionCommentData);
    } else {
      setUpdatedComentsList(commentObj as ReactionCommentData[]);
    }
    if (typeof onAddComment == 'function') onAddComment();
  }

  const onLikeCountClick = () => {
    if (!disableFooterInteraction && !disableLikeDetailsInteraction) {
      setLikesListModalOpen(true);
    } else if (onLikeDetailsPress) {
      onLikeDetailsPress()
    }
  }

  const onCommentCountClick = () => {
    if (!disableFooterInteraction && !disableCommentDetailsInteraction) {
      setCommentModalOpen(true);
    } else if (onCommentDetailsPress) {
      onCommentDetailsPress();
    }
  }

  return (
    <div className={`like-comment-wrap ${customLikeCommentStyle ? customLikeCommentStyle : ''}`}>
      <div className="row align-items-center">
        <div className="w-50">
          <div className="d-flex align-items-center">
            <div className={`like-icon border-${entityType === ENTITY_TYPE.EYENGAGE ? 'info' : 'danger'} ${postLiked ? `bg-${entityType === ENTITY_TYPE.EYENGAGE ? 'info' : 'danger'}` : ''} cursor-pointer`}
              onClick={postLiked ? onUnLikePress : onLikePress}
            >
              {entityType === ENTITY_TYPE.EYENGAGE ?
                <FaThumbsUp
                  color={postLiked ? '#FFFFFF' : '#297ACC'}
                  height="25px"
                  width="25px"
                /> :
                <Heart
                  color={postLiked ? '#FFFFFF' : '#F25268'}
                  height="22px"
                  width="22px"
                />
              }
            </div>
            {likeCountLocal && likeCountLocal > 0 ? (
              <div onClick={onLikeCountClick} className="like-count cursor-pointer">
                <p className="fs-12 text-black text-black mb-0">
                  {
                    entityType === ENTITY_TYPE.OBSERVATION ?
                      <>{likeCountLocal} {likeCountLocal > 1 ? 'loved' : 'love'}</> :
                      <>{likeCountLocal} {likeCountLocal > 1 ? 'likes' : 'like'}</>
                  }
                </p>
                <LikesListModal
                  isOpen={likesListModalOpen}
                  toggle={() => setLikesListModalOpen(false)}
                  entityType={entityType}
                  entityId={entityId}
                />
              </div>
            ) : null}
          </div>
        </div>
        {showComment ? 
          <div className="w-50">
            <div onClick={onCommentCountClick} className="d-flex justify-content-end align-items-center comment-wrap cursor-pointer">
              {commentCountLocal && commentCountLocal > 0 ?
                <p className="fs-12 text-black text-black mb-0">{commentCountLocal}{' '}{commentCountLocal > 1 ? 'comments' : 'comment'}</p> :
                <>
                  <img src={LocalImages.commentIcon} alt="" />
                  <p className="fs-12 text-black text-black mb-0 mg-l-5">Comment</p>
                </>
              }
              <CommentsModal
                isOpen={commentsModalOpen}
                toggle={() => setCommentModalOpen(false)}
                entityType={entityType}
                entityId={entityId}
                newComment={newComment}
                updatedCommentsList={updatedCommentsList}
                onDeleteComment={onDeleteComment}
                handleAddComment={handleAddComment}
                canLikeComment={canLikeComment} 
              />
            </div>
          </div> : 
          null
        }
      </div>
    </div>
  );
}

export default LikeCommentFooter;

